import _ from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { Prompt } from 'react-router';
import { FormikProvider, useField, useFormik, useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';
import moment from 'moment';
import QuillDelta from 'quill-delta';

import { ChevronLeftIcon } from '@treams/treams-icons';
import { useHistory } from '@treams/treams-routes';
import {
	Button,
	Container,
	Heading,
	Input,
	ItemSelectProps,
	Label,
	Paragraph,
	QuillInput,
	Tags,
	Toolbar,
} from '@treams/treams-ui';

import * as graphql from 'generated/graphql';
import useLocalize from 'packages/kakapo-ui-application/hooks/useLocalize';
import useAuthenticatedUser from 'packages/kakapo-ui-application/hooks/useAuthenticatedUser';
import useFeature, { useFeatureDetails } from 'packages/kakapo-ui-application/hooks/useFeature';
import useCompleteWorkspaceCheckpoint from 'packages/kakapo-ui-application/hooks/useCompleteWorkspaceCheckpoint';
import useViewer from 'packages/kakapo-ui-application/hooks/useViewer';

import SingleGroupSelector from './group-selector/SingleGroupSelector';
import GoalSelector from './goal-selector/GoalSelector';
import GoalTemplateSelector from './goal-templates/GoalTemplateSelector';
import Subgoals from './subgoals/Subgoals';
import QuillText from 'packages/kakapo-ui-kit/components/editors/QuillText';
import RemainingGoalsStatus from './RemainingGoalsStatus';
import GoalTemplates from './goal-templates/GoalTemplates';
import { ModalContext } from '../navigation/ModalNavigation';
import GoalMagic from './goal-magic/GoalMagic';
import SubgoalsContainer from './subgoals/SubgoalsContainer';

function FeasibilityPicker() {
	const { t } = useTranslation();

	const [{ value }, , { setValue }] = useField('feasibility');

	const options = [
		{
			value: graphql.GoalFeasibility.WillMakeIt,
			title: t('goals.show.feasibility.willMakeIt'),
			color: 'green',
		},
		{
			value: graphql.GoalFeasibility.CloseCall,
			title: t('goals.show.feasibility.closeCall'),
			color: 'yellow',
		},
		{
			value: graphql.GoalFeasibility.WontMakeIt,
			title: t('goals.show.feasibility.wontMakeIt'),
			color: 'red',
		},
	];

	return (
		<Tags.Item.Select
			label={t('goals.show.feasibility.label')}
			options={options}
			selectedValue={value}
			onChange={setValue}
		/>
	);
}

function CompetencyPicker() {
	const { t } = useTranslation();
	const { localize } = useLocalize();
	const authenticatedUser = useAuthenticatedUser();

	const { data } = graphql.useJobPositionsQuery({
		variables: {
			id: authenticatedUser.id,
			status: graphql.JobPositionStatus.Active,
		},
	});

	const [fetch, query] = graphql.useCompetenciesLazyQuery({
		variables: {
			isArchived: false,
			jobTitleIDs: data?.user.jobPositions.map((jobPosition) => jobPosition.jobTitle.id),
			first: 1000,
		},
	});

	const [{ value }, , { setValue }] = useField<graphql.CompetencyGoal>('competency');

	return (
		<Tags.Item.Select
			label={t('goals.show.competency.label')}
			options={
				query.data &&
				_.sortBy(
					query.data.competencies.edges
						.filter(({ node }) => node.attachableToGoal)
						.map(({ node }) => ({
							value: node.id,
							title: localize(node.title).text,
							context: (
								<>
									<Heading>{localize(node.title).text}</Heading>
									<Paragraph mt={2}>
										<QuillText quill={localize(node.description ?? [])?.text} />
									</Paragraph>
								</>
							),
						})),
					'title',
				)
			}
			onChange={(value) => {
				const competency = query
					.data!.competencies.edges.map(({ node }) => node)
					.find((node) => node.id === value)! as graphql.Competency;
				setValue({ competency, competencyLevelIndex: 0, appraisals: [] });
			}}
			fetch={fetch}
			selectedOption={
				value
					? {
							value: value.competency.id,
							title: localize(value.competency.title).text,
					  }
					: undefined
			}
		/>
	);
}

function CompetencyLevelPicker() {
	const { t } = useTranslation();
	const { localize } = useLocalize();

	const [{ value }, , { setValue }] = useField<graphql.CompetencyGoal>('competency');

	const query = graphql.useCompetencyQuery({
		variables: { id: value?.competency.id },
		skip: !value,
	});

	if (!value) {
		return null;
	}

	const levels = value.competency.levels ?? query.data?.competency.levels;

	if (!levels) {
		return <Tags.Item.Skeleton label={t('goals.show.competencyLevel.label')} />;
	}

	return (
		<Tags.Item.Select
			label={t('goals.show.competencyLevel.label')}
			options={levels
				?.filter(({ enabled }) => enabled)
				.map(({ level, criteria }) => ({
					value: level.id,
					title: localize(level.title).text,
					context: (
						<>
							<Heading>{localize(level.title).text}</Heading>
							<Paragraph mt={2}>
								<QuillText quill={localize(criteria ?? [])?.text} />
							</Paragraph>
						</>
					),
				}))}
			onChange={(newValue) => {
				const index = levels.findIndex(({ level }) => level.id === newValue);
				setValue({ competency: value.competency, competencyLevelIndex: index, appraisals: [] });
			}}
			selectedValue={levels?.[value.competencyLevelIndex].level.id}
		/>
	);
}

const TemplatePickerContainer = styled(Box)`
	border-radius: 5px;
	background: ${(props) => props.theme.colors.lightBlueLight};
`;

function TemplatePicker(props: { userID: string }) {
	const history = useHistory();
	const templateID = useMemo(() => {
		const search = new URLSearchParams(history.location.search);
		return search.get('template_id');
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const [template, setTemplate] = useState<graphql.GoalTemplate | undefined>(undefined);
	const formik = useFormikContext();
	const { localize } = useLocalize();

	useEffect(() => {
		if (!template) {
			return;
		}

		formik.setFieldValue('title', template.title);
		formik.setFieldValue('description', localize(template.description).text);
		formik.setFieldValue('competency', template.competency as graphql.CompetencyGoal);

		if (!_.isNil(template.relativeDueDate)) {
			formik.setFieldValue('dueDate', moment().add(template.relativeDueDate, 'days').toDate());
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [template?.id]);

	if (!templateID) {
		return null;
	}

	return (
		<TemplatePickerContainer p={3} mt={2} display="none">
			<GoalTemplateSelector
				userID={props.userID}
				template={template}
				onChange={() => setTemplate(template)}
				onLoad={(templates) => {
					const template = templates.find((template) => template.id === templateID);

					if (!template) {
						return;
					}

					setTemplate(template);
				}}
			/>
		</TemplatePickerContainer>
	);
}

function getChecklistItemProps(checklistItem: graphql.GoalChecklistItem) {
	const { type } = checklistItem.progress;

	const allowBounds = [
		graphql.GoalChecklistItemType.Range,
		graphql.GoalChecklistItemType.Currency,
	].includes(type);

	return ['id', 'title', 'description', 'progress.value', 'progress.type', 'dueDate'].concat(
		allowBounds ? ['progress.lower', 'progress.upper'] : [],
	);
}

function EditGoal(props: {
	goal?: graphql.Goal;
	userID?: string;
	returnURL: string;
	onReset: () => void;
}) {
	const history = useHistory();
	const { language, localize } = useLocalize();
	const viewer = useAuthenticatedUser();
	const completeOrganizationGoal = useCompleteWorkspaceCheckpoint('organization.goals');
	const hasGoalsAI = useFeature(graphql.FeatureName.GoalsAi);
	const params = new URLSearchParams(history.location.search);

	const [create, { loading: loadingCreate }] = graphql.useCreateGoalMutation();
	const [update, { loading: loadingUpdate }] = graphql.useUpdateIndividualGoalMutation();
	const [move, { loading: loadingMove }] = graphql.useMoveGoalMutation();
	const loading = loadingCreate || loadingUpdate || loadingMove;

	const [groups, setGroups] = useState<graphql.Group[]>([]);
	const [parentGoal, setParentGoal] = useState(props.goal?.parent ?? undefined);

	const [saved, setSaved] = useState(false);

	const initialValues = {
		title: props.goal?.title ?? [{ languageID: language.id, text: '' }],
		description: props.goal?.description ?? { ops: [] },
		momentID: params.get('moment_id') ?? undefined,
		dueDate: props.goal?.dueDate ?? undefined,
		feasibility: props.goal?.feasibility ?? undefined,
		competency: props.goal?.competency ?? undefined,
		subgoals: _.chain(props.goal?.checklistItems ?? [])
			.keyBy('id')
			.value(),
		subgoalIDs: (props.goal?.checklistItems ?? []).map(({ id }) => id),
		groupID:
			props.goal?.owner.__typename === 'Group' ? props.goal?.owner.id : params.get('group_id'),
		workspaceID:
			props.goal?.owner.__typename === 'Workspace'
				? props.goal?.owner.id
				: params.get('workspace_id'),
		visibility: props.goal?.visibility,
	};

	const formik = useFormik({
		initialValues,
		onSubmit: async (values) => {
			const input: Partial<graphql.CreateGoalInput> = {
				title: values.title,
				description: values.description,
				dueDate: values.dueDate,
				momentID: values.momentID,
				linkedGoalDriverIDs: props.goal?.linkedGoalDrivers?.map(({ id }) => id) ?? [],
				linkedGoalIDs: props.goal?.linkedGoals?.map(({ id }) => id) ?? [],
				feasibility: values.feasibility ?? undefined,
				competency:
					formik.values.workspaceID || formik.values.groupID
						? undefined
						: values.competency
						? {
								competencyID: values.competency.competency.id,
								competencyLevelIndex: values.competency.competencyLevelIndex,
						  }
						: undefined,
				checklistItems: values.subgoalIDs.map(
					(id) =>
						_.pick(
							values.subgoals[id],
							getChecklistItemProps(values.subgoals[id]),
						) as graphql.GoalChecklistItem,
				),
				visibility: values.visibility,
			};

			setSaved(true);

			if (props.goal) {
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				await update({ variables: { input: { id: props.goal.id, ...input } as any } });

				if (parentGoal?.id !== props.goal.parent?.id) {
					await move({
						variables: {
							input: {
								goalID: props.goal.id,
								parentID: parentGoal?.id,
							},
						},
					});
				}

				history.push(
					`${props.userID ? `/users/${props.userID}/goals` : `/goals`}/${props.goal.id}`,
				);
			} else {
				const group = groups.find(({ id }) => id === formik.values.groupID);

				const owner = formik.values.workspaceID
					? {
							ownerID: formik.values.workspaceID,
							ownerType: graphql.GoalOwnerType.Workspace,
					  }
					: group
					? {
							ownerID: group.id,
							ownerType:
								group.type === graphql.GroupType.Team
									? graphql.GoalOwnerType.Team
									: graphql.GoalOwnerType.Department,
					  }
					: {
							ownerID: props.userID ?? viewer.id,
							ownerType: graphql.GoalOwnerType.User,
					  };

				const response = await create({
					variables: {
						input: {
							...owner,
							...input,
						} as graphql.CreateGoalInput,
					},
				});

				const goalID = response.data!.createGoal.goal.id;

				if (parentGoal) {
					await move({
						variables: {
							input: {
								goalID,
								parentID: parentGoal.id,
							},
						},
					});
				}

				if (formik.values.workspaceID) {
					completeOrganizationGoal();
				}

				if (
					values.momentID &&
					response.data?.createGoal.goal.moment?.status !== graphql.MomentStatus.Completed
				) {
					history.push(
						props.userID
							? `/users/${props.userID}/goals/new?moment_id=${values.momentID}`
							: `/goals/new?moment_id=${values.momentID}`,
					);
					props.onReset();
				} else {
					history.push(props.userID ? `/users/${props.userID}/goals` : `/goals/${goalID}`);
				}
			}
		},
		enableReinitialize: true,
	});

	const { t } = useTranslation();
	const hasCompetencyGoals = useFeature(graphql.FeatureName.CompetencyGoals);
	const hasTeamGoals = useFeature(graphql.FeatureName.TeamGoals);

	const visibilityOptions: ItemSelectProps['options'] = [
		{
			title: t('goals.show.visibility.options.private.label'),
			value: graphql.GoalVisibility.Private,
			context: t('goals.show.visibility.options.private.description'),
		},
		{
			title: t('goals.show.visibility.options.public.label'),
			value: graphql.GoalVisibility.Public,
			context: t('goals.show.visibility.options.public.description'),
		},
	];

	return (
		<FormikProvider value={formik}>
			<Prompt when={formik.dirty && !saved} message={t('goals.edit.prompt')} />
			<Container.Sticky
				py={4}
				header={
					<>
						{formik.values.momentID && <RemainingGoalsStatus momentID={formik.values.momentID} />}

						<Toolbar>
							<ModalContext.Consumer>
								{(value) =>
									!value.isModal && (
										<Button
											icon={<ChevronLeftIcon />}
											link={props.returnURL}
											title={t('goals.edit.toolbar.cancel')}
											type="secondary"
										/>
									)
								}
							</ModalContext.Consumer>
							<Toolbar.Spacing />
							<Button
								loading={loading}
								title={
									props.goal
										? t('goals.edit.toolbar.save')
										: formik.values.momentID
										? t('goals.edit.toolbar.createAndContinue')
										: t('goals.edit.toolbar.create')
								}
								onClick={() => formik.submitForm()}
							/>
						</Toolbar>
					</>
				}
			>
				<Box>
					<Box>
						<Label mb={2}>{t('goals.edit.field.title')}</Label>
						<Flex alignItems="center">
							<Input
								autoFocus
								value={localize(formik.values.title).text}
								onChange={(text: string) => {
									formik.setFieldValue('title', [{ languageID: language.id, text }]);
								}}
								flexGrow={1}
							/>
							<Box ml={3}>
								{hasGoalsAI && (
									<GoalMagic
										initialInput={params.get('magic_prompt') ?? undefined}
										onTakeSuggestion={async (suggestion) => {
											formik.setFieldValue('title', [
												{ languageID: language.id, text: suggestion.title },
											]);
											formik.setFieldValue('description', {
												ops: [
													{
														insert: suggestion.description,
													},
												],
											} as graphql.Scalars['QuillDelta']);

											const subgoalIDs = suggestion.subgoals.map((subgoal) => {
												const subgoalID = _.uniqueId();

												formik.setFieldValue(`subgoals.${subgoalID}`, {
													title: [{ languageID: language.id, text: subgoal }],
													description: [{ languageID: language.id, text: new QuillDelta() }],
													progress: {
														type: graphql.GoalChecklistItemType.Checkbox,
														value: 0,
														lower: undefined,
														upper: undefined,
													},
													dueDate: undefined,
												});

												return subgoalID;
											});

											formik.setFieldValue('subgoalIDs', subgoalIDs);
										}}
									/>
								)}
							</Box>
						</Flex>
						<TemplatePicker userID={props.userID ?? viewer.id} />
					</Box>
					{!props.goal && !formik.values.workspaceID && params.has('group_id') && (
						<Box mt={3}>
							<Label mb={2}>{t('goals.edit.field.group')}</Label>
							<SingleGroupSelector.Formik
								name="groupID"
								canSelect={(group) => group.viewerCanCreateGoal}
								onLoad={setGroups}
							/>
						</Box>
					)}

					<Tags mt={2} flexWrap="wrap">
						{props.goal && (
							<Box mt={2}>
								<FeasibilityPicker />
							</Box>
						)}
						<Box mt={2}>
							<Tags.Item.DatePicker
								label={t('goals.show.dueDate.label')}
								value={formik.values.dueDate}
								onChange={(date) => formik.setFieldValue('dueDate', date)}
							/>
						</Box>

						{hasTeamGoals && (
							<>
								<Box mt={2}>
									<GoalSelector
										personalGoals={!formik.values.groupID}
										goal={parentGoal}
										onChange={setParentGoal}
									/>
								</Box>

								{parentGoal && (
									<Box mt={2}>
										<Tags.Item.Select
											label={t('goals.show.visibility.label')}
											options={visibilityOptions}
											selectedValue={formik.values.visibility}
											onChange={(visibility) => formik.setFieldValue('visibility', visibility)}
										/>
									</Box>
								)}
							</>
						)}
					</Tags>

					{!(formik.values.workspaceID || formik.values.groupID) && hasCompetencyGoals && (
						<>
							<Label mt={3}>{t('goals.edit.field.competency')}</Label>

							<Tags mt={1}>
								<CompetencyPicker />
								<CompetencyLevelPicker />
							</Tags>
						</>
					)}

					<Box mt={3}>
						<Label mb={2}>{t('goals.edit.field.description')}</Label>
						<QuillInput
							value={formik.values.description ?? { ops: [] }}
							onChange={(text) => formik.setFieldValue('description', text)}
							placeholder={t('goals.edit.description.placeholder')}
						/>
					</Box>
				</Box>
			</Container.Sticky>
			<SubgoalsContainer>
				<Subgoals editable />
			</SubgoalsContainer>
		</FormikProvider>
	);
}

export default function Goal(props: { userID?: string; goalID?: string }) {
	const history = useHistory();
	const params = new URLSearchParams(history.location.search);
	const hasJobGoalTemplates = useFeatureDetails(graphql.FeatureName.JobGoalTemplates);

	const [scratch, setScratch] = useState(false);

	const query = graphql.useIndividualGoalQuery({
		variables: { id: props.goalID! },
		skip: !props.goalID,
	});

	const viewer = useViewer();

	const returnURL = (() => {
		// Editing existing goal
		if (props.goalID) {
			if (props.userID) {
				return `/users/${props.userID}/goals/${props.goalID}`;
			}
			return `/goals/${props.goalID}`;
		}

		// Create team/department goal
		if (params.has('group_id')) {
			return '/goals/collective';
		}

		// Create workspace goal
		if (params.has('workspace_id')) {
			return '/goals/organization';
		}

		// Create personal goal for other user
		if (props.userID) {
			return `/users/${props.userID}/goals`;
		}

		// Create personal goal for viewer
		return '/goals';
	})();

	if (hasJobGoalTemplates.loading) {
		return null;
	}

	if (!props.goalID) {
		if (
			!scratch &&
			!params.has('template_id') &&
			!params.has('workspace_id') &&
			!params.has('group_id') &&
			!params.has('magic_prompt') &&
			hasJobGoalTemplates.available
		) {
			return (
				<GoalTemplates
					userID={props.userID ?? viewer!.id}
					returnURL={returnURL}
					onScratch={() => setScratch(true)}
				/>
			);
		} else {
			return (
				<EditGoal userID={props.userID} returnURL={returnURL} onReset={() => setScratch(false)} />
			);
		}
	}

	if (query.data) {
		return (
			<EditGoal
				goal={query.data.goal as graphql.Goal}
				userID={props.userID}
				returnURL={returnURL}
				onReset={() => setScratch(false)}
			/>
		);
	} else {
		return null;
	}
}
